import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

/**
 * Apple Sign-In button for the web.
 *
 * Uses Apple's official JS SDK (loaded from public/index.html) with
 * `usePopup: true` so the SPA can capture the id_token via promise —
 * avoids the form_post → CORS trap you'd get with a redirect flow.
 *
 * Props:
 *   - onSuccess({ id_token, user }): called after Apple returns the token.
 *   - onError(err): optional error callback.
 *   - label: button text (i18n).
 */
export default function AppleSignInButton({ onSuccess, onError, label = "Continue with Apple" }) {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const initedRef = useRef(false);

  useEffect(() => {
    // Apple JS SDK is loaded async from index.html; poll briefly until it
    // exposes window.AppleID, then init exactly once.
    let cancelled = false;
    const clientId = process.env.REACT_APP_APPLE_SERVICE_ID;
    const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI;
    if (!clientId || !redirectURI) {
      // Missing config — hide the button rather than crash.
      return;
    }
    const start = Date.now();
    const tryInit = () => {
      if (cancelled) return;
      if (window.AppleID && window.AppleID.auth && !initedRef.current) {
        try {
          window.AppleID.auth.init({
            clientId,
            scope: "name email",
            redirectURI,
            state: "lumioos-web",
            usePopup: true,
          });
          initedRef.current = true;
          setReady(true);
        } catch (e) {
          if (onError) onError(e);
        }
        return;
      }
      if (Date.now() - start < 8000) {
        setTimeout(tryInit, 200);
      }
    };
    tryInit();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  const handleClick = async () => {
    if (!ready || loading || !window.AppleID?.auth) return;
    setLoading(true);
    try {
      const resp = await window.AppleID.auth.signIn();
      const id_token = resp?.authorization?.id_token;
      if (!id_token) throw new Error("no_id_token");
      // `user` is only present on the very first sign-in per Apple ID.
      const user = resp?.user || null;
      if (onSuccess) await onSuccess({ id_token, user });
    } catch (err) {
      // popup_closed_by_user / user_cancelled_authorize is normal — swallow.
      const code = err?.error || err?.code || "";
      if (String(code).includes("popup_closed") || String(code).includes("cancel")) {
        // silent
      } else if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!process.env.REACT_APP_APPLE_SERVICE_ID) return null;

  // Hide the WEB Apple button when running inside the native mobile app
  // WebView — the native shell already renders its own Apple Sign-In button
  // (via expo-apple-authentication), and showing both makes users see two
  // duplicate "Sign in with Apple" buttons on iOS/Android.
  //
  // Detection is conservative — false positives just mean we hide one
  // extra button, which is safe. We check for:
  //   1. React Native WebView global (`window.ReactNativeWebView`)
  //   2. iOS WKWebView message handlers
  //   3. UA strings that native shells commonly inject (LumioosApp, wv)
  if (typeof window !== "undefined") {
    const hasRNWebView = !!window.ReactNativeWebView;
    const hasWKHandlers = !!(window.webkit && window.webkit.messageHandlers);
    const ua = (window.navigator && window.navigator.userAgent) || "";
    const isAndroidWebView = / wv\)/i.test(ua); // Android WebView tag
    const isCustomShell = /Lumioos|lumioos|Expo/i.test(ua);
    if (hasRNWebView || hasWKHandlers || isAndroidWebView || isCustomShell) {
      return null;
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!ready || loading}
      data-testid="apple-signin-btn"
      className="w-full inline-flex items-center justify-center gap-2 py-[10px] rounded-[4px] bg-black text-white text-[14px] font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed border border-black"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          {/* Apple logo SVG — official mark, inline so we don't need a network fetch. */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span>{label}</span>
        </>
      )}
    </button>
  );
}
